import { useMemo, type FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import VideoSound from '@wix/da-ds/pkg/Icons/12x12/VideoSound';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { VideoModel } from '../../../../types/store';
import { useDispatch, useSelector } from 'react-redux';
import { updateVideoCreatorModel } from '../../../../actions/studio/videoCreator';
import { getVideoCreatorModel } from '../../../../selectors/studio';
import { getModelPropertiesByType } from '../../ModelProperties';
import s from './ModelButton.scss';

const CACHE_BUSTER = 4;

const withCacheBuster = (url: string) => {
  try {
    const u = new URL(url);
    u.searchParams.set('v', String(CACHE_BUSTER));
    return u.toString();
  } catch {
    return `${url}?v=${CACHE_BUSTER}`;
  }
};

export interface Props {
  type: VideoModel;
}

const ModelButton: FC<Props> = ({ type }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedModel = useSelector(getVideoCreatorModel);
  const selected = selectedModel === type;

  const model = useMemo(() => {
    return getModelPropertiesByType(type);
  }, [type]);

  const priceStr = useMemo(() => {
    if (!model) {
      return '';
    }
    let str = '';
    for (let i = 0; i < model.priceRange; i++) {
      str += t('studio.videoCreator.modelPriceCharacter');
    }
    return str;
  }, [model, t]);

  if (!model) {
    return null;
  }

  return (
    <div
      className={cn(
        s['root'],
        selected && s['selected'],
        !selected && s['unselected']
      )}
      onClick={() => {
        dispatch(updateVideoCreatorModel(type));
      }}
    >
      <img
        className={s['background-gif']}
        src={withCacheBuster(model.backgroundUrl)}
      />
      {selected && (
        <div className={cn(s['gif-gradient'], s['selected-gradient'])} />
      )}
      {!selected && (
        <div className={cn(s['gif-gradient'], s['unselected-gradient'])} />
      )}
      <div className={s['info']}>
        <div className={s['upper-info']}>
          {model.isCore && (
            <CoreSymbol className={s['core-indicator']} size={IconSize.SMALL} />
          )}
        </div>
        <div className={s['lower-info']}>
          <img className={s['logo']} src={withCacheBuster(model.logo)} />
          <span className={s['name']}>
            {t(`studio.videoCreator.modelName.${model.type}`)}
          </span>
          <div className={s['properties']}>
            <div className={cn(s['price-range'], s['property'])}>
              {priceStr}
            </div>
            {model.showDescriptor && (
              <div className={cn(s['descriptor'], s['property'])}>
                {t(`studio.videoCreator.modelDescriptor.${model.type}`)}
              </div>
            )}
            {model.isMature && (
              <div className={cn(s['mature'], s['property'])}>
                {t('studio.videoCreator.modelMature')}
              </div>
            )}
            {model.hasSound !== 'no' && (
              <div className={cn(s['sound'], s['property'])}>
                <IconButton
                  icon={VideoSound}
                  size={'small'}
                  title={t('studio.videoCreator.label.audio')}
                  aria-label={t('studio.videoCreator.label.audio')}
                  className={s['sound-icon']}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ModelButton.displayName = 'ModelButton';
export default ModelButton;
